
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, Mutation } from 'vuex-class';
import Modal from '../Modal.vue';
import Version from '../Version.vue';
import { SectionInterface } from '@/Interfaces/SectionInterface';
import router from "@/router";
import { fileName, media, sections } from '@/Services/helpers/VuexGetters';
import { setMediaAndLength } from "@/Services/helpers/VuexMutations";
import TranscriptionParseService from '@/Services/TranscriptionParserService';
import UndoService from "@/Services/UndoService";

@Component({
  components: {
    Modal,
    Version,
  },
})
export default class FileImport extends Vue {

  @Getter('media') media: HTMLMediaElement;
  @Getter('sections') sections: SectionInterface[];
  @Getter('transcriptionLength') transcriptionLength: number;
  @Getter('mediaLength') mediaLength: number;
  @Getter('fileName') fileName: string;
  @Getter('undoQueue') undoQueue: UndoService[];

  @Mutation('setMediaObjectUrl') setMediaObjectUrl: (objectUrl: string) => void;
  @Mutation('setSections') setSections: (sections: SectionInterface[]) => void;
  @Mutation('setTranscriptionLength') setTranscriptionLength: (transcriptionLength: number) => void;
  @Mutation('setMediaLength') setMediaLength: (mediaLength: number) => void;
  @Mutation('updateFileName') updateFileName: (fileName: string) => void;
  @Mutation('updateAudioFileName') updateAudioFileName: (fileName: string) => void;

  @Action('cleanTextData') cleanTextData: () => void;
  @Action('cleanMediaData') cleanMediaData: () => void;
  @Action('deleteEmptySectionsAndOrderSectionIds') deleteEmptySectionsAndOrderSectionIds: () => void;

  showModal: boolean = false;
  modalHeader: string = null;
  modalBody: string = null;

  key: number = 1; // templeito elementui priskiriamas dinaminis key, kad galėtume refreshinti file inut lauką

  // stebim įkeltus failus ir redirectinam į transkripcijos tekstą
  @Watch('$store.getters.sections')
  onSectionsChange(val, oldval) {
    if (oldval.length === 0 && val.length > 0 && this.media !== null) {
      this.$nextTick(() => {
        router.push({ name: 'transcription-edit', params: { locale: this.$i18n.locale }});
      })
    }
  }
  @Watch('$store.getters.media')
  onMediaChange(val, oldval) {
    if (oldval === null && val !== null && this.sections.length > 0) {
      this.$nextTick(() => {
        router.push({ name: 'transcription-edit', params: { locale: this.$i18n.locale }});
      })
    }
  }

  openNewTranscription() {
    if (this.undoQueue && this.undoQueue.length) {
      // @ts-ignore
      this.$vueConfirm.confirm(
        {
          title: this.$t("messages.confirm").toString(),
          auth: false,
          message: this.$t("import.confirm_new_files").toString(),
          button: {
            no: this.$t("messages.no").toString(),
            yes: this.$t("messages.yes").toString(),
          },
        },
        (confirm) => {
          if (confirm) {
            this.cleanTextData();
          }
        },
      )
    } else {
      this.cleanTextData();
    }
  }

  openNewAudio() {
    this.cleanMediaData();
  }

  async onFileInput(files: FileList) {
    for (const file of files) {
      const objType = file.type.split('/')[0];
      if (['audio', 'video'].includes(objType)) {
        this.cleanMediaData();
        const objectUrl = URL.createObjectURL(file);
        if (objType === 'video') {
          this.setMediaObjectUrl(objectUrl);
        }
        setMediaAndLength(new Audio(objectUrl));
        this.updateAudioFileName(file.name);
      } else if (objType === 'text') {
        await this.openTextFileWithConfirmation(file);
      } else {
        this.$notify({
          text: this.$t("messages.invalid_file_format", {file_name: file.name}).toString(),
          type: 'error',
        })
      }
    }
  }

  /**
   * patikrinam ar nera redaguotu operaciju
   */
  async openTextFileWithConfirmation(file: File) {
    if (this.undoQueue && this.undoQueue.length) {
      // @ts-ignore
      this.$vueConfirm.confirm(
        {
          title: this.$t("messages.confirm").toString(),
          auth: false,
          message: this.$t("import.confirm_new_files").toString(),
          button: {
            no: this.$t("messages.no").toString(),
            yes: this.$t("messages.yes").toString(),
          },
        },
        async (confirm) => {
          if (!confirm) {
            return;
          }
          await this.verifyAndOpenTextFile(file)
        },
      )
    } else {
      await this.verifyAndOpenTextFile(file)
    }
  }

  /**
   * patikrinam ar transkripcijoje nera klaidu ir paklausiam ar importuoti su klaidomis
   */
  async verifyAndOpenTextFile(file: File) {
    this.cleanTextData();
    const tp = new TranscriptionParseService(file);
    const sections = await tp.getSections();
    const fileName = file.name.replace('.txt', '');
    const transcriptionLength = await tp.getTranscriptionLength()

    if (tp.parseErrors.length > 0) {
      // @ts-ignore
      this.$vueConfirm.confirm(
        {
          title: this.$t("messages.confirm").toString(),
          auth: false,
          message: this.$t("import.imported_file_errors_message", {file_name: file.name, error_count: tp.parseErrors.length}).toString(),
          button: {
            no: this.$t("messages.view_errors").toString(),
            yes: this.$t("messages.import_anyway").toString(),
          },
        },
        async (confirm) => {
          if (!confirm) {
            this.showModal = true;
            this.modalBody = tp.parseErrors.map((error) =>
              this.$t("import.line", {file_name: file.name}).toString() + ': ' + error.line + ' ' + error.message + ' (' + error.content + ')').join('\n');
            this.modalHeader = this.$t("import.file_parse_errors_header", {file_name: file.name}).toString();
          } else {
            this.updateStateWithImportedFileData(sections, fileName, transcriptionLength)
          }
        },
      )
    } else {
      this.updateStateWithImportedFileData(sections, fileName, transcriptionLength)
    }
  }

  /**
   * su klaidomis sutiko, arba klaidų nebuvo, reikia updatinti state
   *
   * @param sections
   * @param fileName
   * @param transcriptonLength
   */
  updateStateWithImportedFileData(sections: SectionInterface[], fileName: string, transcriptonLength: number) {
    this.setSections(sections);
    this.deleteEmptySectionsAndOrderSectionIds();
    this.updateFileName(fileName)
    this.setTranscriptionLength(transcriptonLength);
  }

  async readMediaFile(file: File): Promise<string> {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = (event: any) => resolve(event.target.result);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    })
  }
}
